import type { TradeAccountOption } from "../contexts/ActiveBrokerAccountContext";
import { isImportOnlyBrokerName } from "./brokerLiveSync";

export type TradeAccountSourcePick = Pick<
  TradeAccountOption,
  "accountSource" | "lastBrokerSyncAt" | "lastCsvImportAt"
>;

type TradeAccountResyncPick = TradeAccountSourcePick & Pick<TradeAccountOption, "brokerName">;

function hasMeaningfulIso(val?: string | null): boolean {
  return val != null && String(val).trim() !== "";
}

/**
 * Raw `source` from GET /broker/trade-accounts, with a narrow UI adjustment:
 * Some rows arrive as `synced_trades` (user has broker credentials) but only CSV-backed trades exist
 * (`last_csv_import_at` set, `last_broker_sync_at` empty). We treat those like CSV-only for pills and actions.
 * If the backend later adds an explicit field (e.g. `primary_ingest`), prefer that here instead.
 */
export function resolveTradeAccountSourceForUi(acc: TradeAccountSourcePick): string {
  const raw = String(acc.accountSource || "").toLowerCase().trim();
  if (
    raw === "synced_trades" &&
    !hasMeaningfulIso(acc.lastBrokerSyncAt) &&
    hasMeaningfulIso(acc.lastCsvImportAt)
  ) {
    return "imported_trades";
  }
  return raw;
}

export function isTradeAccountCsvOnlyForUi(
  acc: TradeAccountSourcePick & { brokerName?: string | null }
): boolean {
  if (acc.brokerName && isImportOnlyBrokerName(acc.brokerName)) return true;
  const s = resolveTradeAccountSourceForUi(acc);
  return s === "imported_trades" || s === "csv_import";
}

export function isTradeAccountBrokerBackedForUi(acc: TradeAccountSourcePick): boolean {
  const s = resolveTradeAccountSourceForUi(acc);
  return s === "credentials" || s === "broker_list" || s === "synced_trades";
}

/** Account has Tessera trade data — belongs in Connected accounts. */
export function isTradeAccountConnectedSyncedForUi(acc: TradeAccountSourcePick): boolean {
  const s = resolveTradeAccountSourceForUi(acc);
  if (s === "synced_trades" || s === "imported_trades") return true;
  // Successful API sync stamps last_broker_sync_at even when no fills landed
  // (BE may leave source as credentials / broker_list).
  return (
    (s === "credentials" || s === "broker_list") &&
    hasMeaningfulIso(acc.lastBrokerSyncAt)
  );
}

export function filterConnectedTradeAccountsForUi<T extends TradeAccountSourcePick>(
  accounts: T[]
): T[] {
  return accounts.filter(isTradeAccountConnectedSyncedForUi);
}

/**
 * Account is discoverable at the broker via API sync but has no Tessera trade data.
 * Import-only accounts are excluded — those are removed entirely, not parked here.
 */
export function isTradeAccountAvailableForUi(
  acc: TradeAccountSourcePick & { brokerName?: string | null }
): boolean {
  if (isTradeAccountCsvOnlyForUi(acc)) return false;

  const s = resolveTradeAccountSourceForUi(acc);
  if (s === "imported_trades" || s === "csv_import") return false;
  if (hasMeaningfulIso(acc.lastBrokerSyncAt)) return false;
  if (s === "broker_list") return true;
  if (s === "credentials" && !hasMeaningfulIso(acc.lastCsvImportAt)) {
    return true;
  }
  return false;
}

/** Whether broker/API re-sync is meaningful for this account (sidebar, account picker). */
export function canResyncTradeAccountForUi(
  acc: TradeAccountResyncPick,
  brokerSupportsAutoSync: boolean
): boolean {
  if (!brokerSupportsAutoSync || isImportOnlyBrokerName(acc.brokerName)) return false;
  return isTradeAccountBrokerBackedForUi(acc);
}

/** Sidebar re-sync CTA: keep when no account is selected; hide for CSV-only / non-sync brokers. */
export function shouldShowSidebarResyncTrades(options: {
  activeAccountCode: string | null;
  activeBrokerName?: string | null;
  activeAccount: TradeAccountResyncPick | null | undefined;
  brokerSupportsAutoSync: boolean | undefined;
  capabilitiesResolved: boolean;
}): boolean {
  if (!options.activeAccountCode?.trim()) return true;
  if (options.activeBrokerName && isImportOnlyBrokerName(options.activeBrokerName)) {
    return false;
  }
  if (!options.activeAccount) return false;
  if (isTradeAccountCsvOnlyForUi(options.activeAccount)) return false;
  if (!options.capabilitiesResolved) {
    // Keep the footer CTA mounted while capabilities load (avoids pop-in on mobile sheet open).
    return isTradeAccountBrokerBackedForUi(options.activeAccount);
  }
  return canResyncTradeAccountForUi(
    options.activeAccount,
    options.brokerSupportsAutoSync === true
  );
}
